import React, { useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Alert,
  DatePicker,
  Statistic,
  Row,
  Col,
  InputNumber,
  Divider,
  Radio,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ShopOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const formatMoney = (value) => `NPR ${Number(value || 0).toFixed(2)}`;

const OfflineSales = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [lineItems, setLineItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  const dateRangeKey = dateRange
    ? `${dateRange[0]?.format('YYYY-MM-DD')}-${dateRange[1]?.format('YYYY-MM-DD')}`
    : 'default';

  const { data: salesResponse = [], isLoading, refetch } = useQuery(
    ['offline-sales', dateRangeKey, searchText, statusFilter],
    async () => {
      const params = new URLSearchParams({ per_page: '100' });
      if (dateRange && dateRange.length === 2) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      if (searchText) params.append('search', searchText);
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`/admin/offline-sales?${params.toString()}`);
      const result = response.data?.data;
      if (result?.data && Array.isArray(result.data)) return result.data;
      if (Array.isArray(result)) return result;
      return [];
    },
    { retry: 1 },
  );

  const { data: customers = [] } = useQuery(
    'offline-sales-customers',
    async () => {
      const response = await api.get('/admin/users/list');
      return response.data?.data ?? response.data?.users ?? response.data ?? [];
    },
    { retry: 1 },
  );

  const { data: products = [] } = useQuery(
    'offline-sales-products',
    async () => {
      const response = await api.get('/admin/products?per_page=1000');
      return response.data?.data?.data ?? response.data?.data ?? response.data ?? [];
    },
    { retry: 1 },
  );

  const createMutation = useMutation(
    async (values) => {
      const payload = {
        customer_id: values.customer_id || null,
        customer_name: values.customer_name || null,
        customer_phone: values.customer_phone || null,
        customer_email: values.customer_email || null,
        purchase_date: values.purchase_date?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        payment_method: values.payment_method || null,
        billing_type: values.billing_type || 'vat',
        vat_rate: values.vat_rate ?? 13,
        notes: values.notes || null,
        line_items: lineItems.map((item) => ({
          name: item.name,
          type: item.type,
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          product_id: item.product_id || null,
        })),
      };
      const response = await api.post('/admin/offline-sales', payload);
      return response.data;
    },
    {
      onSuccess: (data) => {
        message.success(data?.message || 'Offline sale recorded');
        setIsModalVisible(false);
        form.resetFields();
        setLineItems([]);
        queryClient.invalidateQueries('offline-sales');
      },
      onError: (error) => {
        const errors = error.response?.data?.errors;
        const msg = errors
          ? Object.values(errors).flat().join(', ')
          : error.response?.data?.message || 'Failed to record offline sale';
        message.error(msg);
      },
    },
  );

  const generateBillMutation = useMutation(
    async (record) => {
      const response = await api.post(`/admin/offline-sales/${record.id}/generate-bill`, {
        billing_type: record.billing_type || 'standard',
        vat_rate: record.vat_rate ?? 13,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data?.already_exists) {
          message.info(data?.message || 'Bill already generated');
        } else {
          message.success(
            `Bill ${data?.data?.invoice_number || ''} generated from offline sale`,
          );
        }
        queryClient.invalidateQueries('offline-sales');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to generate bill');
      },
    },
  );

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { name: '', type: 'product', product_id: null, quantity: 1, unit_price: 0, total: 0 },
    ]);
  };

  const removeLineItem = (index) => {
    const next = [...lineItems];
    next.splice(index, 1);
    setLineItems(next);
  };

  const updateLineItem = (index, field, value) => {
    const next = [...lineItems];
    next[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      next[index].total = (next[index].quantity || 0) * (next[index].unit_price || 0);
    }
    setLineItems(next);
  };

  const productSubtotal = lineItems
    .filter((i) => i.type === 'product')
    .reduce((sum, i) => sum + (i.total || 0), 0);
  const serviceSubtotal = lineItems
    .filter((i) => i.type === 'service')
    .reduce((sum, i) => sum + (i.total || 0), 0);
  const billingType = Form.useWatch('billing_type', form) || 'vat';
  const vatRate = Form.useWatch('vat_rate', form) ?? 13;

  const vatOnServices = billingType === 'vat_all';
  const taxableBase = vatOnServices ? productSubtotal + serviceSubtotal : productSubtotal;

  const { vatAmount, taxableAmount, subtotal, grandTotal } = useMemo(() => {
    let vat = 0;
    let taxable = productSubtotal;
    let sub = productSubtotal + serviceSubtotal;

    if ((billingType === 'vat' || billingType === 'vat_all') && taxableBase > 0 && vatRate > 0) {
      const baseBeforeVat = taxableBase / (1 + vatRate / 100);
      vat = taxableBase - baseBeforeVat;
      taxable = baseBeforeVat;
      sub = baseBeforeVat + (vatOnServices ? 0 : serviceSubtotal);
    }

    return {
      vatAmount: vat,
      taxableAmount: taxable,
      subtotal: sub,
      grandTotal: taxableBase + (vatOnServices ? 0 : serviceSubtotal),
    };
  }, [billingType, vatRate, productSubtotal, serviceSubtotal, taxableBase, vatOnServices]);

  const totalSales = Array.isArray(salesResponse) ? salesResponse.length : 0;
  const totalRevenue = (Array.isArray(salesResponse) ? salesResponse : []).reduce(
    (sum, s) => sum + Number(s.total_amount || 0),
    0,
  );
  const billedCount = (Array.isArray(salesResponse) ? salesResponse : []).filter(
    (s) => s.status === 'billed',
  ).length;

  const columns = [
    {
      title: 'Sale #',
      dataIndex: 'sale_number',
      key: 'sale_number',
      width: 150,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>{record.customer_name || record.customer?.name || '-'}</Text>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>
            {record.customer_phone || record.customer?.phone_number || ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      ellipsis: true,
      render: (_, record) => {
        const names = (record.line_items || []).map((i) => `${i.name} x${i.quantity}`);
        return <Text>{names.join(', ') || '—'}</Text>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'purchase_date',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      width: 100,
      render: (v) => (v ? <Tag>{String(v).toUpperCase()}</Tag> : '-'),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 110,
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) =>
        record.status === 'billed' ? (
          <Tag color="green">Billed</Tag>
        ) : (
          <Tag color="orange">Recorded</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          {record.status !== 'billed' ? (
            <Tooltip title="Generate invoice from this offline sale">
              <Button
                type="primary"
                size="small"
                icon={<FileTextOutlined />}
                loading={generateBillMutation.isLoading}
                onClick={() => generateBillMutation.mutate(record)}
              >
                Bill
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title={`Invoice #${record.billed_invoice_id || ''}`}>
              <Tag color="green">Invoiced</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <ShopOutlined /> Offline Sales (Shop Counter)
        </Title>
        <Text type="secondary">
          Record counter sales. Stock is deducted immediately; bills can be generated inline or
          merged via the Combined Bill.
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8} md={6}>
          <Card hoverable size="small">
            <Statistic title="Total Sales" value={totalSales} prefix={<ShopOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card hoverable size="small">
            <Statistic
              title="Revenue"
              value={totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card hoverable size="small">
            <Statistic title="Billed" value={billedCount} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Space>
            <Input.Search
              placeholder="Search by sale #, customer name, or phone..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => setSearchText(value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Status"
              allowClear
              value={statusFilter}
              onChange={(v) => setStatusFilter(v || '')}
              style={{ width: 120 }}
            >
              <Option value="recorded">Recorded</Option>
              <Option value="billed">Billed</Option>
            </Select>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={['Start', 'End']}
            />
          </Space>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
              Record Offline Sale
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={Array.isArray(salesResponse) ? salesResponse : []}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} sales` }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        title="Record Offline Sale"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setLineItems([]);
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ billing_type: 'vat', vat_rate: 13, purchase_date: dayjs() }}
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Alert
            message="Stock for catalog products is deducted automatically when the sale is recorded."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Divider orientation="left">Customer</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_id" label="Existing Customer (optional)">
                <Select
                  allowClear
                  showSearch
                  placeholder="Select a customer, or leave blank for walk-in"
                  filterOption={(input, option) =>
                    String(option.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value, option) => {
                    if (value) {
                      form.setFieldsValue({
                        customer_name: option?.name || option?.full_name || '',
                        customer_phone: option?.phone_number || '',
                        customer_email: option?.email || '',
                      });
                    }
                  }}
                >
                  {(Array.isArray(customers) ? customers : []).map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name || c.full_name} {c.phone_number ? `(${c.phone_number})` : ''} -{' '}
                      {c.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customer_name"
                label="Customer Name"
                rules={[{ required: true, message: 'Customer name required' }]}
              >
                <Input placeholder="Walk-in customer name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_phone" label="Phone">
                <Input placeholder="+977-98XXXXXXXX" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_email" label="Email (optional)">
                <Input placeholder="customer@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Sale Details</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="purchase_date" label="Purchase Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="payment_method" label="Payment Method">
                <Select allowClear placeholder="Select payment method">
                  <Option value="cash">Cash</Option>
                  <Option value="card">Card</Option>
                  <Option value="mobile_banking">Mobile Banking</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="qr">QR</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="billing_type" label="Billing Type">
                <Radio.Group>
                  <Radio.Button value="standard">Standard</Radio.Button>
                  <Radio.Button value="vat">VAT (Products)</Radio.Button>
                  <Radio.Button value="vat_all">VAT (Products + Services)</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            {(billingType === 'vat' || billingType === 'vat_all') && (
              <Col span={8}>
                <Form.Item name="vat_rate" label="VAT Rate (%)">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider orientation="left">Line Items</Divider>
          <Row
            gutter={8}
            style={{ marginBottom: 8, fontWeight: 'bold', color: '#666', fontSize: 12 }}
          >
            <Col span={2}>Type</Col>
            <Col span={9}>Product</Col>
            <Col span={3}>Qty</Col>
            <Col span={4}>Unit Price</Col>
            <Col span={4}>Total</Col>
            <Col span={2}></Col>
          </Row>

          {lineItems.map((item, index) => {
            const filteredProducts = products.filter(
              (p) => !item.type || (p.type || 'product') === item.type,
            );
            return (
              <Card key={index} size="small" style={{ marginBottom: 8, background: '#fafafa' }}>
                <Row gutter={8} align="middle">
                  <Col span={2}>
                    <Select
                      value={item.type}
                      onChange={(value) => {
                        updateLineItem(index, 'type', value);
                        updateLineItem(index, 'name', '');
                        updateLineItem(index, 'product_id', null);
                      }}
                      style={{ width: '100%' }}
                    >
                      <Option value="product">Product</Option>
                      <Option value="service">Service</Option>
                    </Select>
                  </Col>
                  <Col span={9}>
                    <Select
                      showSearch
                      placeholder="Search product or type a name..."
                      value={
                        item.product_id || (item.name && !item.product_id ? item.name : undefined)
                      }
                      onChange={(value) => {
                        if (typeof value === 'number') {
                          const product = products.find((p) => p.id === value);
                          updateLineItem(index, 'product_id', value);
                          updateLineItem(index, 'name', product?.name || '');
                          updateLineItem(index, 'unit_price', parseFloat(product?.price || 0));
                        } else {
                          updateLineItem(index, 'product_id', null);
                          updateLineItem(index, 'name', value);
                        }
                      }}
                      filterOption={(input, option) =>
                        String(option.children || '').toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ width: '100%' }}
                    >
                      {filteredProducts.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.name} - NPR {Number(p?.price || 0).toFixed(2)}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      value={item.quantity}
                      onChange={(value) => updateLineItem(index, 'quantity', value || 1)}
                      min={1}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={4}>
                    <InputNumber
                      value={item.unit_price}
                      onChange={(value) => updateLineItem(index, 'unit_price', value || 0)}
                      min={0}
                      precision={2}
                      prefix="NPR"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Input value={Number(item.total || 0).toFixed(2)} disabled prefix="NPR" />
                  </Col>
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      onClick={() => removeLineItem(index)}
                      icon={<DeleteOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            );
          })}

          <Space style={{ marginBottom: 16 }}>
            <Button type="dashed" onClick={addLineItem} icon={<PlusOutlined />}>
              Add Line Item
            </Button>
          </Space>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {billingType === 'vat_all'
                  ? 'VAT is applied to both products and services.'
                  : billingType === 'vat'
                    ? 'VAT (13%) is applied to products only. Services are VAT exempt.'
                    : 'No VAT is applied. Prices are entered as-is.'}
              </Text>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'right' }}>
                {billingType !== 'standard' && (
                  <div>
                    <Text>Taxable Amount:</Text>{' '}
                    <Text strong>{formatMoney(taxableAmount)}</Text>
                  </div>
                )}
                {billingType !== 'vat_all' && (
                  <div>
                    <Text>Tax Exempt Amount (Services):</Text>{' '}
                    <Text strong>{formatMoney(serviceSubtotal)}</Text>
                  </div>
                )}
                <div>
                  <Text>Subtotal:</Text> <Text strong>{formatMoney(subtotal)}</Text>
                </div>
                {vatAmount > 0 && (
                  <div>
                    <Text>VAT ({vatRate}%):</Text> <Text strong>{formatMoney(vatAmount)}</Text>
                  </div>
                )}
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    Total:
                  </Text>{' '}
                  <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                    {formatMoney(grandTotal)}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isLoading}
                icon={<PlusOutlined />}
                disabled={lineItems.length === 0}
              >
                Record Sale (Deduct Stock)
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                  setLineItems([]);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OfflineSales;
